import { Badge,Group, Menu, Text, UnstyledButton } from '@mantine/core';
import { useRouter } from '@tanstack/react-router';
import type { ActiveHat, UserCapabilities } from '@wwaave/shared';
import { useState } from 'react';

import { useAuth } from '../../lib/auth-context';
import { showToast } from '../../lib/toast';

/**
 * HatSwitcher — small chip + dropdown that lets a multi-cap user pick which
 * hat is "active". The chip only renders when the user has more than one
 * capability; users with a single hat see nothing.
 *
 * Clicking a hat:
 *   1. Optimistically flips the local active hat (auth-context).
 *   2. POSTs `/api/auth/active-hat` to persist + rotate the cookie.
 *   3. Invalidates the router so route guards re-evaluate the new hat.
 */

const HAT_LABEL: Record<ActiveHat, string> = {
  artist: 'Artist',
  host: 'Host',
  admin: 'Admin',
};

const HAT_DESCRIPTION: Record<ActiveHat, string> = {
  artist: 'Looking for venues to play.',
  host: 'Hosting touring artists.',
  admin: 'Trust & safety / platform.',
};

export function HatSwitcher({
  compact = false,
  fullWidth = false,
}: {
  compact?: boolean;
  /** When true, render as a full-width sidebar row (label-style chip with
   * background + caret on the right) instead of the compact pill. */
  fullWidth?: boolean;
}) {
  const { capabilities, activeHat, setActiveHat } = useAuth();
  const router = useRouter();
  const [busy, setBusy] = useState(false);

  if (!capabilities || activeHat === null) return null;
  const hats = availableHats(capabilities);
  if (hats.length < 2) return null;

  const onPick = async (hat: ActiveHat) => {
    if (hat === activeHat || busy) return;
    setBusy(true);
    try {
      await setActiveHat(hat);
      // Re-evaluate route guards (they read capabilities + activeHat).
      await router.invalidate();
    } catch (err) {
      showToast({
        kind: 'error',
        body: err instanceof Error ? err.message : 'Could not switch hats.',
      });
    } finally {
      setBusy(false);
    }
  };

  // Sidebar full-width chip — bigger touch target, label aligned left, caret
  // on the right. Used by AppSidebar.tsx at the top of the sidebar.
  const triggerStyle: React.CSSProperties = fullWidth
    ? {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        width: '100%',
        padding: '10px 12px',
        borderRadius: 10,
        border: 'light-dark(1px solid color-mix(in srgb, var(--wwaave-brand-primary) 25%, transparent), 1px solid var(--mantine-color-dark-4))',
        background: 'light-dark(#fff, var(--mantine-color-dark-6))',
        color: 'light-dark(var(--mantine-color-brand-7), var(--mantine-color-brand-4))',
        fontSize: 14,
        fontWeight: 500,
        boxShadow: 'light-dark(0 1px 3px color-mix(in srgb, var(--wwaave-brand-primary) 10%, transparent), none)',
        transition: 'background 120ms ease',
      }
    : {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: compact ? '4px 8px' : '6px 10px',
        borderRadius: 999,
        border: 'light-dark(1px solid color-mix(in srgb, var(--wwaave-brand-primary) 25%, transparent), 1px solid var(--mantine-color-dark-4))',
        background: 'light-dark(#fff, var(--mantine-color-dark-6))',
        color: 'light-dark(var(--mantine-color-brand-7), var(--mantine-color-brand-4))',
        fontSize: 13,
        fontWeight: 500,
        boxShadow: 'light-dark(0 1px 3px color-mix(in srgb, var(--wwaave-brand-primary) 10%, transparent), none)',
        transition: 'background 120ms ease',
      };

  return (
    <Menu shadow="md" position={fullWidth ? 'bottom-start' : 'bottom-end'} withArrow width={fullWidth ? 240 : 220}>
      <Menu.Target>
        <UnstyledButton
          aria-label={`Active hat: ${HAT_LABEL[activeHat]}. Click to change.`}
          style={triggerStyle}
        >
          <Group gap={8} wrap="nowrap" align="center">
            <Badge
              size="xs"
              variant="filled"
              color="brand.6"
              circle
              style={{ width: 8, height: 8, padding: 0, flexShrink: 0 }}
            />
            <Text size="sm" fw={fullWidth ? 600 : 500} style={{ color: 'inherit' }}>
              {HAT_LABEL[activeHat]}
              {fullWidth ? ' workspace' : ''}
            </Text>
          </Group>
          <Text style={{ color: 'inherit' }} size="xs" aria-hidden>
            ▾
          </Text>
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>Switch hat</Menu.Label>
        {hats.map((hat) => (
          <Menu.Item
            key={hat}
            onClick={() => void onPick(hat)}
            disabled={busy}
            rightSection={
              hat === activeHat ? (
                <Text size="xs" c="brand.7" fw={600}>
                  Active
                </Text>
              ) : null
            }
          >
            <Group gap={4} wrap="nowrap">
              <Text fw={hat === activeHat ? 600 : 500} size="sm">
                {HAT_LABEL[hat]}
              </Text>
            </Group>
            <Text size="xs" c="ink.6">
              {HAT_DESCRIPTION[hat]}
            </Text>
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}

function availableHats(caps: UserCapabilities): ActiveHat[] {
  const hats: ActiveHat[] = [];
  if (caps.isArtist) hats.push('artist');
  if (caps.isHost) hats.push('host');
  if (caps.isAdmin) hats.push('admin');
  return hats;
}
